import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import { useAuth } from '@/contexts/AuthContext';

type Tab = 'quick' | 'login' | 'register';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, signUp, signInWithGoogle } = useAuth();

  const [activeTab, setActiveTab] = useState<Tab>('quick');
  const [quickName, setQuickName] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regClass, setRegClass] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [showGreeting, setShowGreeting] = useState(false);
  const [greetingName, setGreetingName] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotContact, setForgotContact] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleQuickLogin = () => {
    if (!quickName.trim()) return;
    localStorage.setItem('bahnar_user', JSON.stringify({ name: quickName.trim(), type: 'quick' }));
    setGreetingName(quickName.trim());
    setShowGreeting(true);
    setTimeout(() => navigate('/games'), 2500);
  };

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) return;
    setLoading(true);
    setError('');
    const { error: err } = await signIn(loginEmail, loginPassword);
    setLoading(false);
    if (err) {
      setError('Email hoặc mật khẩu không đúng. Vui lòng thử lại.');
      return;
    }
    const name = loginEmail.split('@')[0];
    localStorage.setItem('bahnar_user', JSON.stringify({ name, email: loginEmail, type: 'full' }));
    setGreetingName(name);
    setShowGreeting(true);
    setTimeout(() => navigate('/games'), 2500);
  };

  const handleRegister = async () => {
    if (!regName || !regEmail || !regPassword) return;
    setLoading(true);
    setError('');
    const { error: err } = await signUp(regEmail, regPassword, {
      full_name: regName,
      phone: regPhone,
      class: regClass,
    });
    setLoading(false);
    if (err) {
      setError(err);
      return;
    }
    localStorage.setItem('bahnar_user', JSON.stringify({
      name: regName.trim(),
      phone: regPhone,
      email: regEmail,
      class: regClass,
      type: 'full',
    }));
    setGreetingName(regName.trim());
    setShowGreeting(true);
    setTimeout(() => navigate('/games'), 2500);
  };

  const handleForgotSend = async () => {
    if (!forgotContact.trim()) return;
    setForgotSent(true);
    setTimeout(() => {
      setForgotMode(false);
      setForgotSent(false);
      setForgotContact('');
    }, 3000);
  };

  if (showGreeting) {
    return <GreetingScreen name={greetingName} />;
  }

  if (forgotMode) {
    return (
      <div className="min-h-screen bg-primary-cream flex items-center justify-center px-6">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full">
          <button onClick={() => setForgotMode(false)} className="flex items-center gap-1.5 text-sm text-primary-brown-light hover:text-primary-orange cursor-pointer mb-5">
            <i className="ri-arrow-left-line"></i> Quay lại đăng nhập
          </button>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center bg-primary-orange/10 rounded-2xl">
              <i className="ri-lock-unlock-line text-primary-orange text-xl"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-primary-brown-dark">Quên mật khẩu</h3>
              <p className="text-xs text-primary-brown-light">Lấy lại qua email</p>
            </div>
          </div>
          {forgotSent ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-mail-send-fill text-2xl text-green-500"></i>
              </div>
              <p className="font-bold text-primary-brown-dark mb-1">Đã gửi mã xác nhận!</p>
              <p className="text-sm text-primary-brown-light">Vui lòng kiểm tra email của bạn.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-primary-brown-light mb-4">
                Nhập email đã đăng ký — chúng mình sẽ gửi link đặt lại mật khẩu.
              </p>
              <input
                type="email"
                value={forgotContact}
                onChange={e => setForgotContact(e.target.value)}
                placeholder="Email của bạn"
                className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors mb-4"
              />
              <button
                onClick={handleForgotSend}
                disabled={!forgotContact.trim()}
                className="w-full py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                Gửi link đặt lại mật khẩu
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-cream">
      <Header />
      <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-12">
        <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="hidden lg:flex flex-col items-center gap-6">
            <img
              src="https://static.readdy.ai/image/1cc4dff2fab325f8b161c01cf2cdaccb/fe96155d68f269116561abf4e98fbf22.png"
              alt="Chào mừng bạn"
              className="w-80 h-80 object-cover object-top rounded-3xl"
            />
            <div className="text-center">
              <h2 className="text-2xl font-bold text-primary-brown-dark mb-2">Chào mừng bạn!</h2>
              <p className="text-primary-brown-light">Cùng khám phá trò chơi dân gian Bahnar nhé!</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8">
            <div className="flex bg-primary-cream rounded-xl p-1 mb-8 gap-1">
              {([['quick', 'Vào nhanh'], ['login', 'Đăng nhập'], ['register', 'Đăng ký']] as [Tab, string][]).map(([tab, label]) => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setError(''); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab
                      ? 'bg-primary-orange text-white'
                      : 'text-primary-brown-light hover:text-primary-brown-dark'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 'quick' && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-primary-orange/10 rounded-full mx-auto mb-3">
                    <i className="ri-user-smile-fill text-3xl text-primary-orange"></i>
                  </div>
                  <h3 className="text-xl font-bold text-primary-brown-dark">Chơi ngay!</h3>
                  <p className="text-sm text-primary-brown-light mt-1">Chỉ cần nhập tên là có thể vào chơi ngay</p>
                </div>
                <input
                  type="text"
                  placeholder="Nhập tên của bạn (VD: Ksor Bình)"
                  value={quickName}
                  onChange={e => setQuickName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleQuickLogin()}
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm focus:outline-none focus:border-primary-orange transition-colors text-primary-brown-dark"
                />
                <button
                  onClick={handleQuickLogin}
                  disabled={!quickName.trim()}
                  className="w-full py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
                >
                  Bắt đầu chơi ngay!
                </button>
                <p className="text-center text-xs text-primary-brown-light">
                  Vào nhanh sẽ không lưu tiến độ.
                  <button onClick={() => setActiveTab('register')} className="text-primary-orange hover:underline ml-1 cursor-pointer">
                    Đăng ký để lưu thành tích
                  </button>
                </p>
              </div>
            )}

            {activeTab === 'login' && (
              <div className="space-y-5">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-primary-brown-dark">Đăng nhập</h3>
                  <p className="text-sm text-primary-brown-light mt-1">Đăng nhập để lưu tiến độ và thành tích</p>
                </div>
                <button
                  onClick={() => signInWithGoogle()}
                  className="w-full py-3 flex items-center justify-center gap-3 border-2 border-primary-brown-light/20 rounded-xl hover:border-primary-orange/40 hover:bg-primary-orange/5 transition-all cursor-pointer whitespace-nowrap font-semibold text-primary-brown-dark text-sm"
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                  Đăng nhập bằng Google
                </button>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-primary-brown-light/20"></div>
                  <span className="text-xs text-primary-brown-light">hoặc</span>
                  <div className="flex-1 h-px bg-primary-brown-light/20"></div>
                </div>
                <input
                  type="email"
                  placeholder="Email của bạn"
                  value={loginEmail}
                  onChange={e => { setLoginEmail(e.target.value); setError(''); }}
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm focus:outline-none focus:border-primary-orange transition-colors text-primary-brown-dark"
                />
                <input
                  type="password"
                  placeholder="Mật khẩu"
                  value={loginPassword}
                  onChange={e => { setLoginPassword(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm focus:outline-none focus:border-primary-orange transition-colors text-primary-brown-dark"
                />
                <button
                  onClick={() => setForgotMode(true)}
                  className="text-sm text-primary-orange hover:underline cursor-pointer"
                >
                  Quên mật khẩu?
                </button>
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl">
                    <i className="ri-error-warning-fill text-red-500 text-sm"></i>
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
                <button
                  onClick={handleLogin}
                  disabled={!loginEmail || !loginPassword || loading}
                  className="w-full py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
                >
                  {loading ? 'Đang đăng nhập...' : 'Đăng nhập'}
                </button>
                <p className="text-center text-xs text-primary-brown-light">
                  Chưa có tài khoản?
                  <button onClick={() => setActiveTab('register')} className="text-primary-orange hover:underline ml-1 cursor-pointer">
                    Đăng ký ngay
                  </button>
                </p>
              </div>
            )}

            {activeTab === 'register' && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-primary-brown-dark">Tạo tài khoản</h3>
                  <p className="text-sm text-primary-brown-light mt-1">Đăng ký để lưu thành tích và tiến độ học</p>
                </div>
                <button
                  onClick={() => signInWithGoogle()}
                  className="w-full py-3 flex items-center justify-center gap-3 border-2 border-primary-brown-light/20 rounded-xl hover:border-primary-orange/40 hover:bg-primary-orange/5 transition-all cursor-pointer whitespace-nowrap font-semibold text-primary-brown-dark text-sm"
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                  Đăng ký bằng Google
                </button>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-primary-brown-light/20"></div>
                  <span className="text-xs text-primary-brown-light">hoặc đăng ký bằng email</span>
                  <div className="flex-1 h-px bg-primary-brown-light/20"></div>
                </div>
                <input
                  type="text"
                  placeholder="Họ và tên"
                  value={regName}
                  onChange={e => setRegName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm focus:outline-none focus:border-primary-orange transition-colors text-primary-brown-dark"
                />
                <input
                  type="tel"
                  placeholder="Số điện thoại (VD: 0900000000)"
                  value={regPhone}
                  onChange={e => setRegPhone(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm focus:outline-none focus:border-primary-orange transition-colors text-primary-brown-dark"
                />
                <input
                  type="text"
                  placeholder="Lớp (VD: 5A, 6B...)"
                  value={regClass}
                  onChange={e => setRegClass(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm focus:outline-none focus:border-primary-orange transition-colors text-primary-brown-dark"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={regEmail}
                  onChange={e => { setRegEmail(e.target.value); setError(''); }}
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm focus:outline-none focus:border-primary-orange transition-colors text-primary-brown-dark"
                />
                <input
                  type="password"
                  placeholder="Mật khẩu (ít nhất 6 ký tự)"
                  value={regPassword}
                  onChange={e => { setRegPassword(e.target.value); setError(''); }}
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm focus:outline-none focus:border-primary-orange transition-colors text-primary-brown-dark"
                />
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl">
                    <i className="ri-error-warning-fill text-red-500 text-sm"></i>
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
                <button
                  onClick={handleRegister}
                  disabled={!regName || !regEmail || !regPassword || loading}
                  className="w-full py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
                >
                  {loading ? 'Đang tạo tài khoản...' : 'Tạo tài khoản'}
                </button>
                <p className="text-center text-xs text-primary-brown-light">
                  Đã có tài khoản?
                  <button onClick={() => setActiveTab('login')} className="text-primary-orange hover:underline ml-1 cursor-pointer">
                    Đăng nhập
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function GreetingScreen({ name }: { name: string }) {
  return (
    <div className="min-h-screen bg-primary-cream flex items-center justify-center">
      <div className="text-center">
        <img
          src="https://readdy.ai/api/search-image?query=Adorable%20chibi%20Bahnar%20ethnic%20girl%20jumping%20joyfully%20waving%20both%20hands%2C%20big%20happy%20smile%2C%20traditional%20Bahnar%20clothing%20with%20colorful%20patterns%2C%20warm%20orange%20yellow%20colors%2C%20confetti%20and%20stars%20floating%20around%2C%20celebration%20mood%2C%20kawaii%20style%20illustration%2C%20cream%20background&width=300&height=320&seq=greetjump&orientation=portrait"
          alt="Chào bạn"
          className="w-56 h-56 object-cover object-top mx-auto mb-6 rounded-full"
        />
        <h1 className="text-4xl font-bold text-primary-brown-dark mb-3">
          Chào mừng, <span className="text-primary-orange">{name}</span>!
        </h1>
        <p className="text-xl text-primary-brown-light mb-2">Đang đưa bạn đến Túi Trò Chơi...</p>
        <div className="flex justify-center gap-2 mt-6">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-3 h-3 rounded-full bg-primary-orange"
              style={{ animation: `bounce 1s infinite ${i * 0.2}s` }} />
          ))}
        </div>
      </div>
    </div>
  );
}
